export const ADDRESS_CONSTRAINTS = {
  line1: {
    maxLength: 200,
    errorMessage: "Street address must be 200 characters or less",
  },
  line2: {
    maxLength: 200,
    errorMessage: "Address line 2 must be 200 characters or less",
  },
  city: {
    maxLength: 50,
    errorMessage: "City must be 50 characters or less",
  },
  state: {
    maxLength: 50,
    errorMessage: "State must be 50 characters or less",
  },
  postalCode: {
    maxLength: 50,
    errorMessage: "Postal code must be 50 characters or less",
  },
  country: {
    maxLength: 2,
    pattern: /^[A-Z]{2}$/,
    errorMessage: "Country must be a 2-letter ISO code",
  },
} as const;

function trimAndCap(
  value: string | undefined | null,
  maxLength: number,
): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

export function normalizeCity(
  value: string | undefined | null,
): string | undefined {
  return trimAndCap(value, ADDRESS_CONSTRAINTS.city.maxLength);
}

export function normalizeLine1(
  value: string | undefined | null,
): string | undefined {
  return trimAndCap(value, ADDRESS_CONSTRAINTS.line1.maxLength);
}

export function normalizeLine2(
  value: string | undefined | null,
): string | undefined {
  return trimAndCap(value, ADDRESS_CONSTRAINTS.line2.maxLength);
}

export function normalizeCountry(
  value: string | undefined | null,
): string | undefined {
  if (!value || value.trim() === "") return undefined;
  const normalized = value.trim().toUpperCase();
  if (!ADDRESS_CONSTRAINTS.country.pattern.test(normalized)) return undefined;
  return normalized;
}

export function validateLine1(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 && trimmed.length <= ADDRESS_CONSTRAINTS.line1.maxLength
  );
}

export function validateLine2(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length === 0 ||
    trimmed.length <= ADDRESS_CONSTRAINTS.line2.maxLength
  );
}

export function validateCity(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 && trimmed.length <= ADDRESS_CONSTRAINTS.city.maxLength
  );
}

export function validateCountry(value: string): boolean {
  return ADDRESS_CONSTRAINTS.country.pattern.test(value.trim().toUpperCase());
}

export interface OneLineAddressInput {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

const DOMESTIC_COUNTRY_ALIASES: ReadonlySet<string> = new Set([
  "US",
  "USA",
  "UNITED STATES",
  "UNITED STATES OF AMERICA",
]);

const NAMED_COUNTRY_CODES: ReadonlyMap<string, string> = new Map([
  ["CANADA", "CA"],
  ["MEXICO", "MX"],
]);

export function isoCountryCode(value: string | undefined | null): string {
  const country = value?.trim().toUpperCase();
  if (!country || DOMESTIC_COUNTRY_ALIASES.has(country)) return "US";
  const code = NAMED_COUNTRY_CODES.get(country) ?? normalizeCountry(country);
  if (!code) {
    throw new Error(`The address country "${value}" is not a recognised country. Use a two-letter code such as US.`);
  }
  return code;
}

export function formatAddressLocality(address: OneLineAddressInput): string {
  const region = [address.state, address.postalCode].filter(Boolean).join(" ");
  return [address.city, region].filter(Boolean).join(", ");
}

export function formatAddressLines(address: OneLineAddressInput): string[] {
  const street = [address.line1, address.line2].filter(Boolean).join(", ");
  const country = address.country?.trim().toUpperCase();
  const foreignCountry = country && !DOMESTIC_COUNTRY_ALIASES.has(country) ? country : undefined;
  return [street, formatAddressLocality(address), foreignCountry].filter(
    (line): line is string => Boolean(line),
  );
}

export function formatAddressOneLine(address: OneLineAddressInput): string {
  return formatAddressLines(address).join(", ");
}

const TRAILING_COUNTRY = /[,\s]+(?:usa|us|united states(?: of america)?)\.?$/i;

function alphanumeric(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function stripTrailingLocality(line1: string, locality: string): string | undefined {
  let index = line1.length;
  let consumed = "";
  while (index > 0 && consumed.length < locality.length) {
    const character = line1.charAt(index - 1).toLowerCase();
    if (/[a-z0-9]/.test(character)) consumed = character + consumed;
    index -= 1;
  }
  if (consumed !== locality) return undefined;
  const head = line1.slice(0, index);
  if (!/[,\s]$/.test(head)) return undefined;
  const street = head.replace(/[,\s]+$/, "");
  return street || undefined;
}

export function stripRepeatedLocality(
  line1: string,
  address: { city?: string; state?: string; postalCode?: string },
): string {
  const city = alphanumeric(address.city ?? "");
  const postalCode = alphanumeric(address.postalCode ?? "");
  if (!city || !postalCode) return line1;
  const locality = city + alphanumeric(address.state ?? "") + postalCode;
  const trimmed = line1.trim();
  for (const candidate of [trimmed, trimmed.replace(TRAILING_COUNTRY, "")]) {
    const street = stripTrailingLocality(candidate, locality);
    if (street !== undefined) return street;
  }
  return line1;
}
